import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Reads a `?highlight=<eventId>` search param and provides helpers
 * for panels to auto-expand sections and scroll to the target element.
 *
 * The param is consumed (removed) once the panel calls `clearHighlight()`.
 */
export function useHighlightParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const scrollAttemptRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHighlight = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('highlight');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /** Scroll to the highlighted element with retries, then clear the param. */
  const scrollToHighlighted = useCallback(() => {
    if (!highlightId) return;
    let retries = 8;
    const attempt = () => {
      const el = document.querySelector(`[data-event-id="${CSS.escape(highlightId)}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const target = el as HTMLElement;
        target.style.animation = 'border-pulse-amber 0.6s ease-in-out 3';
        target.addEventListener('animationend', () => { target.style.animation = ''; }, { once: true });
        clearHighlight();
      } else if (retries > 0) {
        retries--;
        scrollAttemptRef.current = setTimeout(attempt, 250);
      }
    };
    // Small initial delay to let React render after state changes
    scrollAttemptRef.current = setTimeout(attempt, 100);
  }, [highlightId, clearHighlight]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollAttemptRef.current) clearTimeout(scrollAttemptRef.current);
    };
  }, []);

  return { highlightId, clearHighlight, scrollToHighlighted };
}
